import { ChallengeService } from '@duck/api-definitions'
import {
    CommandLineIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    ClockIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import type { ComponentType } from 'react'
import { TranslationKey, useTar } from '@/modules'
import { CompletedChallenge } from '../FeedbackDialog'
import { challengeDifficultyDisplayMap, getChallengeDifficultyColorAccentVariant } from '@/utils/challengeData.utils'
import { formatWorkTime } from '@/utils/time.utils'
import { Progress, Badge } from '@duck/ui'
import { feedbackStatusTranslationMap } from '../../utils/feedback.utils'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { DevPrint } from '@components/DevPrint'
import { AllowedWeaponsPills } from '@components/AllowedWeaponsPills'

interface CompletedChallengeItem {
    icon: ComponentType<{ className?: string }>
    color: string,
    text: TranslationKey
}

const getFeedbackStatusInfo = (feedbackStatus?: ChallengeService.FeedbackStatusEnum): CompletedChallengeItem => {
    if (!feedbackStatus) {
        return {
            text: 'challenges.feedbackError',
            color: 'bg-muted/10 text-muted border-muted/20',
            icon: ExclamationTriangleIcon
        }
    }
    if (feedbackStatus === ChallengeService.FeedbackStatusEnum.Processed) {
        return {
            text: 'challenges.processingStageReady',
            color: 'bg-success/10 text-success border-success/20',
            icon: CheckCircleIcon
        }
    }

    return {
        text: feedbackStatusTranslationMap[feedbackStatus],
        color: 'bg-accent/10 text-accent border-accent/20',
        icon: ClockIcon
    }
}

const getFeedbackProgress = (feedbackStatus?: ChallengeService.FeedbackStatusEnum): number => {
    if (!feedbackStatus) return 0

    const progressMap: Record<ChallengeService.FeedbackStatusEnum, number> = {
        [ChallengeService.FeedbackStatusEnum.Unprocessed]: 0,
        [ChallengeService.FeedbackStatusEnum.Processing1]: 6,
        [ChallengeService.FeedbackStatusEnum.Processing2]: 12,
        [ChallengeService.FeedbackStatusEnum.Processing3]: 18,
        [ChallengeService.FeedbackStatusEnum.Processing4]: 24,
        [ChallengeService.FeedbackStatusEnum.Processing5]: 30,
        [ChallengeService.FeedbackStatusEnum.Processing6]: 36,
        [ChallengeService.FeedbackStatusEnum.Processing7]: 42,
        [ChallengeService.FeedbackStatusEnum.Processing8]: 48,
        [ChallengeService.FeedbackStatusEnum.Processing9]: 54,
        [ChallengeService.FeedbackStatusEnum.Processing10]: 60,
        [ChallengeService.FeedbackStatusEnum.Processing11]: 66,
        [ChallengeService.FeedbackStatusEnum.Processing12]: 72,
        [ChallengeService.FeedbackStatusEnum.Processing13]: 78,
        [ChallengeService.FeedbackStatusEnum.Processing14]: 84,
        [ChallengeService.FeedbackStatusEnum.Processing15]: 90,
        [ChallengeService.FeedbackStatusEnum.Processed]: 100
    }

    return progressMap[feedbackStatus] || 0
}

const isProcessingStatus = (feedbackStatus?: ChallengeService.FeedbackStatusEnum): boolean => {
    if (!feedbackStatus) return false

    const processingStatuses: ChallengeService.FeedbackStatusEnum[] = [
        ChallengeService.FeedbackStatusEnum.Processing1,
        ChallengeService.FeedbackStatusEnum.Processing2,
        ChallengeService.FeedbackStatusEnum.Processing3,
        ChallengeService.FeedbackStatusEnum.Processing4,
        ChallengeService.FeedbackStatusEnum.Processing5,
        ChallengeService.FeedbackStatusEnum.Processing6,
        ChallengeService.FeedbackStatusEnum.Processing7,
        ChallengeService.FeedbackStatusEnum.Processing8,
        ChallengeService.FeedbackStatusEnum.Processing9,
        ChallengeService.FeedbackStatusEnum.Processing10,
        ChallengeService.FeedbackStatusEnum.Processing11,
        ChallengeService.FeedbackStatusEnum.Processing12,
        ChallengeService.FeedbackStatusEnum.Processing13,
        ChallengeService.FeedbackStatusEnum.Processing14,
        ChallengeService.FeedbackStatusEnum.Processing15
    ]

    return processingStatuses.includes(feedbackStatus)
}

const formatAttemptNumber = (attempt: number): string => {
    const suffixes = ['th', 'st', 'nd', 'rd']
    const v = attempt % 100
    return attempt + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]) + ' try'
}

interface CompletedChallengeCardProps {
    completedChallenge: CompletedChallenge
    attemptNumber?: number
    onShowResults: (challenge: CompletedChallenge) => void
    onShowTaskInfo: (task: ChallengeService.Task) => void
}

export const CompletedChallengeCard = ({
    completedChallenge,
    attemptNumber,
    onShowResults,
    onShowTaskInfo
}: CompletedChallengeCardProps) => {
    const { t } = useTar()
    const { challenge, task, icon } = completedChallenge
    const IconComponent = icon || CommandLineIcon
    const feedbackStatusInfo = getFeedbackStatusInfo(challenge.feedbackStatus)
    const FeedbackIcon = feedbackStatusInfo.icon
    const isFeedbackProcessed = challenge.feedbackStatus === ChallengeService.FeedbackStatusEnum.Processed

    return (
        <div
            className="bg-card rounded-lg shadow-sm border border-border p-6 hover:shadow-md transition-shadow duration-200"
        >
            {/* Challenge Icon & Category */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-success/10 rounded-lg">
                        <IconComponent className="w-6 h-6 text-success" />
                    </div>
                    <AllowedWeaponsPills
                        allowedWeapons={task.allowedWeapons}
                        chosenWeapon={challenge.workState?.chosenWeapon}
                    />
                </div>

                {/* Difficulty Badge and Attempt Badge */}
                <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getChallengeDifficultyColorAccentVariant(task.difficulty)}`}>
                        {t(challengeDifficultyDisplayMap[task.difficulty])}
                    </span>
                    {attemptNumber !== undefined && (
                        <Badge variant="outline" className="text-xs">
                            {formatAttemptNumber(attemptNumber)}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Challenge Title */}
            <h2 className="text-xl font-semibold text-foreground mb-3 h-12 leading-6 flex items-start">
                <span className="line-clamp-2">
                    {task.name}
                </span>
            </h2>

            <DevPrint label="challengeId" value={challenge.id} />

            {/* Challenge Description */}
            <p className="text-muted-foreground text-sm mb-4 leading-relaxed line-clamp-1">
                {task.teaser}
            </p>

            {/* Completion Status */}
            <div className="flex items-center text-success text-xs mb-4">
                <CheckCircleIcon className="w-4 h-4 me-1" />
                <span className="font-medium">{t('challenges.completedStatus')}</span>
                <span className="mx-2">•</span>
                <span>{t('challenges.finishedIn', { time: formatWorkTime(challenge.workTime, t) })}</span>
            </div>

            {/* End Time */}
            {challenge.endTime && (
                <div className="flex items-center text-muted-foreground text-xs mb-4">
                    <ClockIcon className="w-4 h-4 me-1" />
                    <RelativeDateTime isoDateString={challenge.endTime} makeLabel={(time) => t('userFeedback.completedTIME', { time })} />
                </div>
            )}

            {/* Feedback Status */}
            <div className="mb-6 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FeedbackIcon className="w-4 h-4" />
                        <span className="text-xs font-medium">{t('challenges.aiFeedbackStatus')}</span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${feedbackStatusInfo.color}`}>
                        {t(feedbackStatusInfo.text)}
                    </span>
                </div>

                {isProcessingStatus(challenge.feedbackStatus) && (
                    <Progress value={getFeedbackProgress(challenge.feedbackStatus)} className="h-2" />
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
                <button
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${isFeedbackProcessed
                        ? 'bg-success hover:bg-success/80 text-white'
                        : 'bg-muted/20 text-muted cursor-not-allowed'
                        }`}
                    disabled={!isFeedbackProcessed}
                    onClick={() => {
                        if (isFeedbackProcessed) {
                            onShowResults(completedChallenge)
                        }
                    }}
                >
                    <CheckCircleIcon className="w-4 h-4" />
                    <span>{t('challenges.viewResults')}</span>
                </button>

                <button
                    className="flex items-center justify-center px-3 py-2 border border-border rounded-md text-sm font-medium bg-card text-muted-foreground hover:bg-accent/10 hover:text-accent transition-colors duration-200"
                    onClick={() => onShowTaskInfo(task)}
                >
                    <InformationCircleIcon className="w-5 h-5" />
                </button>
            </div>
        </div>
    )
}

